/**
 * Sentry browser-side initialisation for the Vite build.
 *
 * Imported as the FIRST import in src/main.tsx so Sentry is active before
 * any other module loads.
 *
 * Replaces @sentry/nextjs (Next.js App Router convention) with @sentry/react.
 * The @sentry/vite-plugin in vite.config.ts handles source-map upload at
 * build time (T07).
 *
 * EIE floor:
 *   - no console.* — log via Sentry breadcrumbs
 *   - scrubEvent applied as beforeSend to strip PII before transmission
 */

import * as Sentry from '@sentry/react'
import { scrubEvent, denyUrls, ignoreErrors } from './lib/sentry'

// Vite-build path: use import.meta.env directly.
// Next.js App Router uses sentry.server.config.ts / sentry.client.config.ts
// at the root (auto-injected by @sentry/nextjs).
const dsn =
  import.meta.env?.VITE_SENTRY_DSN ??
  import.meta.env?.NEXT_PUBLIC_SENTRY_DSN_VETERANS_SHOP
const environment = import.meta.env?.VITE_SENTRY_ENVIRONMENT ?? import.meta.env?.MODE

if (dsn) {
  Sentry.init({
    dsn,
    environment,
    integrations: [
      Sentry.browserTracingIntegration(),
      Sentry.replayIntegration({
        maskAllText: true,
        blockAllMedia: true,
      }),
    ],
    tracesSampleRate: environment === 'production' ? 0.1 : 0,
    replaysSessionSampleRate: 0,
    replaysOnErrorSampleRate: environment === 'production' ? 0.1 : 0,
    denyUrls,
    ignoreErrors,
    // scrubEvent uses an internal SentryEvent type that is structurally
    // compatible with @sentry/react's ErrorEvent; the cast is safe.
    beforeSend: (scrubEvent as unknown) as Parameters<typeof Sentry.init>[0]['beforeSend'],
  })
}
